// ShopModal.js
import React, { useState, useEffect } from 'react';
import { useCoin } from '../CoinContext';
import { supabase } from '../supabaseClient';
import ssaltcoin from '../assets/ssaltcoin.png';

export default function ShopModal({ open, onClose }) {
  const { currentPoints = 0, setCurrentPoints, walletAddress } = useCoin();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [rewardModal, setRewardModal] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);

  useEffect(() => {
    if (!open) return;
    const fetchProducts = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          setFetchError('Please login');
          return;
        }
        const response = await supabase.functions.invoke('get-shop-items', {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });
        if (response.error) throw response.error;

        const formattedProducts = response.data.map(item => ({
          id: item.id,
          productId: item.product_id,
          name: item.title,
          description: item.description,
          image: item.item_image_url,
          cost: item.price,
          type: item.type,
          limePrice: item.lime_price,
          currency: '🌾',
          probabilities: item.probabilities
        }));

        setProducts(formattedProducts);
      } catch (err) {
        console.error('Error fetching shop items:', err);
        setFetchError(err.message);
      }
    };
    fetchProducts();
  }, [open]);

  const handlePurchase = async (product) => {
    const points = currentPoints || 0;
    if (points < product.cost) {
      alert(`Insufficient coins! (Required: ${product.cost}, Have: ${points})`);
      return;
    }
    if (!walletAddress) {
      alert('Wallet connection required!');
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Please login again');
        setLoading(false);
        return;
      }
      const response = await supabase.functions.invoke('purchase-shop-item', {
        body: { 
          shopItemId: product.id,
          quantity: 1
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      if (response.error) {
        throw new Error(response.error.message || 'Purchase failed');
      }
      const result = response.data;
      setCurrentPoints(result.newPoints);
      setCurrentProduct(product);
      setRewardModal(result.rewards || []);   // ← 배열 저장

    } catch (err) {
      console.error('Purchase error:', err);
      alert('Error occurred during purchase: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase10 = async (product) => {
    const points = currentPoints || 0;
    const totalCost = product.cost;
    if (points < totalCost) {
      alert(`Insufficient coins! (Required: ${totalCost}, Have: ${points})`);
      return;
    }
    if (!walletAddress) {
      alert('Wallet connection required!');
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Please login again');
        setLoading(false);
        return;
      }
      const response = await supabase.functions.invoke('purchase-shop-item', {
        body: {
          shopItemId: product.id,
          quantity: 10
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      if (response.error) {
        throw new Error(response.error.message || 'Purchase failed');
      }
      const result = response.data;
      setCurrentPoints(result.newPoints);
      setCurrentProduct(product);
      setRewardModal(result.rewards || []);   // ← 배열 저장

    } catch (err) {
      console.error('Purchase error:', err);
      alert('Error occurred during purchase: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseN = async (product, quantity) => {
    const points = currentPoints || 0;
    const totalCost = product.cost * quantity;
    if (points < totalCost) {
      alert(`Insufficient coins! (Required: ${totalCost}, Have: ${points})`);
      return;
    }
    if (!walletAddress) {
      alert('Wallet connection required!');
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Please login again');
        setLoading(false);
        return;
      }
      const response = await supabase.functions.invoke('purchase-shop-item', {
        body: {
          shopItemId: product.id,
          quantity: quantity
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      if (response.error) {
        throw new Error(response.error.message || 'Purchase failed');
      }
      const result = response.data;
      setCurrentPoints(result.newPoints);
      setCurrentProduct(product);
      setRewardModal(result.rewards || []);

    } catch (err) {
      console.error('Purchase error:', err);
      alert('Error occurred during purchase: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: '90%',
            maxWidth: 480,
            maxHeight: '85vh',
            background: 'linear-gradient(180deg, #4FC3D4 0%, #3AA8B8 100%)',
            borderRadius: 24,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            position: 'relative',
            padding: '32px 20px 20px',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
            }}
          />
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'rgba(255,255,255,0.3)',
              fontSize: 20,
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 'bold',
              zIndex: 10,
            }}
          >
            ✕
          </button>
          <h2
            style={{
              margin: '0 0 24px 0',
              textAlign: 'center',
              color: '#fff',
              fontSize: 28,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              position: 'relative',
            }}
          >
            • SHOP •
          </h2>
          <div
            style={{
              background: 'rgba(255,255,255,0.95)',
              borderRadius: 16,
              padding: '12px 20px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              position: 'relative',
            }}
          >
            <img
              src={ssaltcoin}
              alt="PetCoin"
              style={{
                width: 32,
                height: 32,
                verticalAlign: 'middle',
              }}
            />
            <span
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#333',
              }}
            >
              {(currentPoints || 0).toLocaleString()}
            </span>
          </div>
          <div
            style={{
              maxHeight: 'calc(85vh - 200px)',
              overflowY: 'auto',
              paddingRight: 4,
              position: 'relative',
            }}
          >
            {fetchError ? (
              <div style={{ textAlign: 'center', color: '#fff', padding: '20px' }}>
                Error loading products.
              </div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#fff', padding: '20px' }}>
                Loading...
              </div>
            ) : (
              products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  userCoin={currentPoints || 0}
                  onPurchase={handlePurchase}
                  onPurchase10={handlePurchase10}
                  onPurchaseN={handlePurchaseN}
                  loading={loading}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {rewardModal && rewardModal.length > 0 && (
  <RewardModal
    rewards={rewardModal}
    onClose={() => {
      setRewardModal(null);
      setCurrentProduct(null);
    }}
    product={currentProduct}
    currentPoints={currentPoints}
    onPurchase10={() => {
      setRewardModal(null);
      if (currentProduct) {
        handlePurchase10(currentProduct);
      }
    }}
    loading={loading}
  />
)}
    </>
  );
}

function ProductCard({ product, userCoin, onPurchase, onPurchase10, onPurchaseN, loading }) {
  const [imageError, setImageError] = useState(false);
  const [showProbModal, setShowProbModal] = useState(false);
  const [probLoading, setProbLoading] = useState(false);
  const [showNDrawModal, setShowNDrawModal] = useState(false);
  const [nDrawCount, setNDrawCount] = useState('');
  const canAfford = userCoin >= product.cost;
  const canAfford10 = userCoin >= product.cost * 10;
  const maxDraws = Math.min(100, Math.floor(userCoin / product.cost));

  const handleOpenProbModal = async () => {
    setShowProbModal(true);
    if (!product.probabilities || product.probabilities.length === 0) {
      setProbLoading(true);
      setProbLoading(false);
    }
  };

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: 16,
        padding: '20px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <button 
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          background: 'rgba(79,195,212,0.13)',
          border: 'none',
          borderRadius: '50%',
          width: 32,
          height: 32,
          cursor: 'pointer',
          fontSize: 17,
          zIndex: 10
        }}
        onClick={handleOpenProbModal}
        title="Gacha Probability"
      >
        %
      </button>
      <div
        style={{
          width: 100,
          height: 100,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          fontSize: imageError ? '48px' : 'inherit',
        }}
      >
        {imageError ? (
          '🎮'
        ) : (
          <img
            src={product.image}
            alt={product.name}
            style={{
              width: '90%',
              height: '90%',
              objectFit: 'contain',
            }}
            onError={() => setImageError(true)}
          />
        )}
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#333',
              marginBottom: 4,
            }}
          >
            {product.name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#666',
            }}
          >
            {product.description}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            background: 'rgba(79, 195, 212, 0.1)',
            borderRadius: 8,
            border: '1px solid rgba(79, 195, 212, 0.3)',
          }}
        >
          <img
            src={ssaltcoin}
            alt="PetCoin"
            style={{
              width: 32,
              height: 32,
              verticalAlign: 'middle',
            }}
          />
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#3AA8B8',
            }}
          >
            {product.cost.toLocaleString()}
          </span>
        </div>
        
        {/* 버튼 컨테이너 */}
        <div
          style={{
            display: 'flex',
            gap: 8,
          }}
        >
          <button
            onClick={() => onPurchase(product)}
            disabled={!canAfford || loading}
            style={{
              flex: 1,
              border: 'none',
              background: canAfford
                ? 'linear-gradient(135deg, #FF8C42 0%, #FF6B35 100%)'
                : '#ccc',
              color: '#fff',
              padding: '12px 20px',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: canAfford && !loading ? 'pointer' : 'not-allowed',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: canAfford
                ? '0 4px 12px rgba(255, 140, 66, 0.3)'
                : 'none',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (canAfford && !loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 16px rgba(255, 140, 66, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = canAfford
                ? '0 4px 12px rgba(255, 140, 66, 0.3)'
                : 'none';
            }}
          >
            {loading ? 'Processing...' : canAfford ? '1 Draw' : 'Insufficient'}
          </button>

          <button
            onClick={() => onPurchase10(product)}
            disabled={!canAfford10 || loading}
            style={{
              flex: 1,
              border: 'none',
              background: canAfford10
                ? 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)'
                : '#ccc',
              color: '#fff',
              padding: '12px 20px',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: canAfford10 && !loading ? 'pointer' : 'not-allowed',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: canAfford10
                ? '0 4px 12px rgba(155, 89, 182, 0.3)'
                : 'none',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (canAfford10 && !loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 16px rgba(155, 89, 182, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = canAfford10
                ? '0 4px 12px rgba(155, 89, 182, 0.3)'
                : 'none';
            }}
          >
            {loading ? 'Processing...' : canAfford10 ? '10 Draw' : 'Insufficient'}
          </button>

          <button
            onClick={() => {
              setNDrawCount('');
              setShowNDrawModal(true);
            }}
            disabled={!canAfford || loading}
            style={{
              flex: 1,
              border: 'none',
              background: canAfford
                ? 'linear-gradient(135deg, #27AE60 0%, #229954 100%)'
                : '#ccc',
              color: '#fff',
              padding: '12px 20px',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: canAfford && !loading ? 'pointer' : 'not-allowed',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: canAfford
                ? '0 4px 12px rgba(39, 174, 96, 0.3)'
                : 'none',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (canAfford && !loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 16px rgba(39, 174, 96, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = canAfford
                ? '0 4px 12px rgba(39, 174, 96, 0.3)'
                : 'none';
            }}
          >
            {loading ? 'Processing...' : canAfford ? 'N Draw' : 'Insufficient'}
          </button>
        </div>
      </div>
      {showProbModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 12000,
          }}
          onClick={() => setShowProbModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 20,
              minWidth: 320,
              padding: 32,
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.16)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Gacha Probability</h3>
            {probLoading || !product.probabilities
              ? (
                <div style={{
                  padding: '32px 0',
                  textAlign: 'center',
                  color: '#3AA8B8',
                  fontWeight: 600,
                  fontSize: 17
                }}>
                  Loading...
                </div>
              )
              : (
                product.probabilities.map(item => (
                  <div key={item.name} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 12
                  }}>
                    <img src={item.image} alt={item.name} style={{
                      width: 32, height: 32, borderRadius: 7, background: '#eee'
                    }} />
                    <span style={{ fontWeight: 700 }}>{item.name}</span>
                    <span style={{
                      color: '#3AA8B8', fontWeight: 500, marginLeft: 'auto'
                    }}>
                      {Number(item.probability * 100).toFixed(2)}%
                    </span>
                    <span style={{
                      fontSize: 12, color: '#666', marginLeft: 7
                    }}>
                      [{item.grade}]
                    </span>
                  </div>
                ))
              )
            }
          </div>
        </div>
      )}
      {showNDrawModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 12000,
          }}
          onClick={() => setShowNDrawModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 20,
              minWidth: 320,
              padding: 32,
              boxShadow: '0 8px 32px rgba(0,0,0,0.16)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>N Draw</h3>
            <div style={{ marginBottom: 16, textAlign: 'center', color: '#666', fontSize: 14 }}>
              How many times do you want to draw?
            </div>
            <div style={{ marginBottom: 16, textAlign: 'center', color: '#3AA8B8', fontSize: 14, fontWeight: 600 }}>
              Max: {maxDraws.toLocaleString()} draws ({(maxDraws * product.cost).toLocaleString()} points)
            </div>
            <input
              type="number"
              min="1"
              max={maxDraws}
              value={nDrawCount}
              onChange={(e) => setNDrawCount(e.target.value)}
              placeholder={`1 ~ ${maxDraws}`}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 16,
                border: '2px solid #3AA8B8',
                borderRadius: 10,
                marginBottom: 12,
                textAlign: 'center',
                boxSizing: 'border-box',
              }}
            />
            {nDrawCount && Number(nDrawCount) > 0 && Number(nDrawCount) <= maxDraws && (
              <div style={{ marginBottom: 16, textAlign: 'center', color: '#333', fontSize: 14 }}>
                Total cost: <strong style={{ color: '#3AA8B8' }}>{(Number(nDrawCount) * product.cost).toLocaleString()}</strong> points
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowNDrawModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '2px solid #ccc',
                  background: '#fff',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: '#666',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const count = Number(nDrawCount);
                  if (count > 0 && count <= maxDraws) {
                    setShowNDrawModal(false);
                    onPurchaseN(product, count);
                  }
                }}
                disabled={!nDrawCount || Number(nDrawCount) < 1 || Number(nDrawCount) > maxDraws}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  background: nDrawCount && Number(nDrawCount) >= 1 && Number(nDrawCount) <= maxDraws
                    ? 'linear-gradient(135deg, #27AE60 0%, #229954 100%)'
                    : '#ccc',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: nDrawCount && Number(nDrawCount) >= 1 && Number(nDrawCount) <= maxDraws ? 'pointer' : 'not-allowed',
                  color: '#fff',
                }}
              >
                Draw
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RewardModal({ rewards, onClose, product, currentPoints, onPurchase10, loading }) {
  // rewards가 배열인지 체크
  if (!Array.isArray(rewards) || rewards.length === 0) return null;

  // 레어리티 순서 (높은 것부터)
  const gradeOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };
  const gradeColors = {
    legendary: { bg: 'linear-gradient(135deg, #FFD700, #FFA500)', border: '#FFD700' },
    epic: { bg: 'linear-gradient(135deg, #9B59B6, #8E44AD)', border: '#9B59B6' },
    rare: { bg: 'linear-gradient(135deg, #3498DB, #2980B9)', border: '#3498DB' },
    common: { bg: 'linear-gradient(135deg, #95A5A6, #7F8C8D)', border: '#95A5A6' },
  };

  // 중복 그룹화 및 레어리티순 정렬
  const groupedRewards = rewards.reduce((acc, reward) => {
    const key = reward.petIndex || reward.name;
    if (!acc[key]) {
      acc[key] = { ...reward, count: 1 };
    } else {
      acc[key].count += 1;
    }
    return acc;
  }, {});

  const sortedRewards = Object.values(groupedRewards).sort((a, b) => {
    const gradeA = gradeOrder[a.grade?.toLowerCase()] ?? 99;
    const gradeB = gradeOrder[b.grade?.toLowerCase()] ?? 99;
    return gradeA - gradeB;
  });

  // 10 Draw가 가능한지 확인 (rewards가 10개이고, 포인트가 충분한 경우)
  const canPurchase10 = rewards.length === 10 && product && currentPoints >= product.cost;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        animation: 'fadeIn 0.3s ease-in-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: 480,
          background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          position: 'relative',
          padding: '32px 20px 24px',
          textAlign: 'center',
          animation: 'scaleIn 0.4s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            margin: '0 0 16px 0',
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
            textShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          🎉 Result ({rewards.length} draws) 🎉
        </h2>

        {/* 여러 개 나열 영역 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
            gap: 12,
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '8px 4px',
          }}
        >
          {sortedRewards.map((reward, idx) => {
            const grade = reward.grade?.toLowerCase() || 'common';
            const colors = gradeColors[grade] || gradeColors.common;
            return (
              <div
                key={idx}
                style={{
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: 12,
                  padding: '8px 6px 10px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  border: `3px solid ${colors.border}`,
                }}
              >
                {reward.count > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      background: colors.bg,
                      color: '#fff',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    }}
                  >
                    x{reward.count}
                  </div>
                )}
                <div
                  style={{
                    width: 70,
                    height: 70,
                    borderRadius: 12,
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 6,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={reward.image}
                    alt={reward.name}
                    style={{
                      width: '90%',
                      height: '90%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#000',
                    maxWidth: 90,
                    wordBreak: 'keep-all',
                    textAlign: 'center',
                  }}
                >
                  {reward.name}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: colors.border,
                    textTransform: 'uppercase',
                    marginTop: 2,
                  }}
                >
                  {grade}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 18, justifyContent: 'center' }}>
          {canPurchase10 && (
            <button
              onClick={onPurchase10}
              disabled={loading}
              style={{
                border: 'none',
                background: loading ? 'linear-gradient(135deg, #CCCCCC, #999999)' : 'linear-gradient(135deg, #FFD700, #FFA500)',
                color: '#fff',
                padding: '12px 36px',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {loading ? '...' : '10 Draw'}
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'rgba(255,255,255,0.95)',
              color: '#FFA500',
              padding: '12px 36px',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            확인
          </button>
        </div>

        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { transform: scale(0.8); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          `}
        </style>
      </div>
    </div>
  );
}
